<!--
  SupplierTabbarNavigation — the Suppliers-Club shell's OWN bottom tabbar.

  This is deliberately a SEPARATE component from TabbarNavigation (the seller
  tabbar), NOT a reuse of it (architecture doc Section 5.3): TabbarNavigation hard-codes
  the seller `<f7-tab id="home|wallet|orders|profile">` stubs, and the supplier
  shell needs GLOBALLY-UNIQUE ids (`suppliers-wall` / `suppliers-feed` /
  `suppliers-profile`) so the two routable-tabs shells can coexist in the one
  main `<f7-view>` route table without `#id` collisions when F7's tab.js
  resolves the active tab by live DOM query during a cross-shell transition.

  Visibility is injected as `showSupplierTabbar` (provided by AppLayout via
  useTabbarVisibility) so the supplier tabbar shows only while the supplier
  shell is active and the seller tabbar stays hidden on /suppliers/* — the two
  are mutually exclusive, club-aware decisions made in one place.

  `standalone`: the same bar drawn on a page OUTSIDE the shell — the supplier
  Poola page (/suppliers/poola/), which is a standalone route, so the shell (and
  its bar) is not in the view there, cold or pushed. Plain links instead of
  routable tab-links (no shell tabs to switch in that page), no <f7-tabs> stubs
  (their ids would duplicate the shell's), and the Poola link drawn active.
-->
<template>
  <f7-toolbar v-show="showSupplierTabbar" tabbar icons bottom>
    <f7-link
      href="/suppliers/"
      icon-f7="cube_box_fill"
      :text="$t('supplier.tabs.wall')"
      :tab-link="!standalone"
      :route-tab-id="standalone ? undefined : 'suppliers-wall'"
    ></f7-link>
    <!--
      «اليوميات» (Daily Orders) tab (supplier-daily-orders design spec 2026-07-05,
      Section 2). Placed right after the Wall (landing) tab. Routable tab — needs BOTH
      halves: this link (tab-link + route-tab-id) AND the matching <f7-tab
      id="suppliers-orders"> stub below. Deliberately NOT the Poola pattern (a
      standalone-route link with no route-tab-id / no stub), which page-replaces
      instead of switching in-shell. Icon tray_full_fill — distinct from the
      Wall's cube_box_fill.
    -->
    <f7-link
      href="/suppliers/orders/"
      icon-f7="tray_full_fill"
      :text="$t('supplier.tabs.dailyOrders')"
      :tab-link="!standalone"
      :route-tab-id="standalone ? undefined : 'suppliers-orders'"
    ></f7-link>
    <!--
      Content feed tab (supplier-content-feed plan 2026-06-26). Placed second,
      after the Wall (landing) tab and before the Poola teaser. Routable tab —
      carries route-tab-id="suppliers-feed" matched by the <f7-tab> stub below.
      Arabic label `supplier.tabs.feed` is added by the copy prompt (P2-D, via
      poolit-voice).
    -->
    <f7-link
      href="/suppliers/feed/"
      icon-f7="square_list_fill"
      :text="$t('supplier.tabs.feed')"
      :tab-link="!standalone"
      :route-tab-id="standalone ? undefined : 'suppliers-feed'"
    ></f7-link>
    <!--
      Poola teaser — supplier-namespaced standalone route. Points at
      /suppliers/poola/ (NOT /poola/) so clubTypeForPath resolves to
      'suppliers', keeping the supplier tabbar visible and hiding the
      seller-only deposit banner on that page. The seller tabbar's Poola
      link continues to point at /poola/.
      Standalone route link (no route-tab-id / no matching <f7-tab>); highlighted
      via .poola-tab-link (brand-accent tint + a small "new" dot). Placed right
      after the wall (the supplier's landing tab) for prominence. RTL-safe.
    -->
    <f7-link
      href="/suppliers/poola/"
      icon-f7="flame_fill"
      :text="$t('tabs.poola')"
      :class="['poola-tab-link', { 'tab-link-active': standalone }]"
      :tab-link="!standalone"
    ></f7-link>
    <!--
      The standalone «طلباتي» (Claims) tab was merged into the «الفرص» window as
      a segment (supplier-opportunities-merged-window plan, 2026-06-27). Its
      pending-claims badge now lives on that طلباتي segment button inside
      SupplierWallPostsPage; /suppliers/claims/ redirects there (routes.js). No
      route-tab-id="suppliers-claims" link OR <f7-tab> stub remains here.
    -->
    <!--
      Hidden while a collector previews a shop (supplier-preview P2-A, design
      Section 4.3): the profile would show the collector's own name and phone
      under the shop's name. The <f7-tab> stub below stays — routable tabs need
      it, and nothing links there during the preview.
    -->
    <f7-link
      v-if="!isSupplierPreview"
      href="/suppliers/profile/"
      icon-f7="person_fill"
      :text="$t('tabs.profile')"
      :tab-link="!standalone"
      :route-tab-id="standalone ? undefined : 'suppliers-profile'"
    ></f7-link>
  </f7-toolbar>
  <f7-tabs v-if="!standalone" routable>
    <f7-tab id="suppliers-wall"></f7-tab>
    <f7-tab id="suppliers-orders"></f7-tab>
    <f7-tab id="suppliers-feed"></f7-tab>
    <f7-tab id="suppliers-profile"></f7-tab>
    <!-- The shop's wallet: no tab-bar link, entered from the profile row. -->
    <f7-tab id="suppliers-wallet"></f7-tab>
  </f7-tabs>
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { useAuthStore } from '@/stores/authStore.js';

defineOptions({
  name: 'SupplierTabbarNavigation',
});

defineProps({
  // Drawn on a page outside the shell (see the header note).
  standalone: { type: Boolean, default: false },
});

// Provided by AppLayout (useTabbarVisibility). Default keeps the tabbar visible
// when mounted in isolation (tests, future re-mounting paths).
const showSupplierTabbar = inject('showSupplierTabbar', ref(true));

const authStore = useAuthStore();
const isSupplierPreview = computed(() => authStore.isSupplierPreview === true);

// The pending-claims badge moved onto the طلباتي segment button in the merged
// «الفرص» window (SupplierWallPostsPage) — it is no longer rendered here.
</script>

<style scoped>
/*
  Poola is the headline marketing surface, so its tab link stands out from the
  neutral siblings. The flame icon carries a warm "fire" gradient (amber -> orange)
  so it reads as a hot, eye-catching highlight even when inactive — the neutral
  siblings are cool gray/purple. We override F7's OWN tabbar-link color variables
  (no specificity war) so the label matches the flame, and keep the small teal
  "new" dot as a complementary pop. Logical positioning (inset-inline-end) keeps
  the dot on the correct side in RTL. Mirrored in TabbarNavigation.vue.
*/
.poola-tab-link {
  --f7-tabbar-link-inactive-color: var(--poolit-warning-600);
  --f7-tabbar-link-active-color: var(--poolit-warning-500);
}

.poola-tab-link :deep(.icon) {
  position: relative;
  overflow: visible;
  /* Fire gradient clipped to the flame glyph. */
  background: linear-gradient(160deg, #fbbf24 0%, #fb923c 55%, #f97316 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

/* Active tap: hotter glow so the highlight still reacts to selection. */
.poola-tab-link.tab-link-active :deep(.icon) {
  filter: drop-shadow(0 0 5px rgba(249, 115, 22, 0.55));
}

.poola-tab-link :deep(.icon)::after {
  content: '';
  position: absolute;
  top: -2px;
  inset-inline-end: -3px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  /* The dot is a solid mark, not part of the clipped glyph. */
  -webkit-text-fill-color: initial;
  background-color: var(--poolit-accent-main);
}
</style>
